import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';
import { IRootState } from 'src/reducers';
import EditBenefitDialog from '../dialogs/benefits/EditBenefitDialog';
import AddBenefitDialog from '../dialogs/benefits/AddBenefitDialog';
import EditYearNoteDialog from '../dialogs/benefits/EditYearNoteDialog';
import EmployeeBenefitCodes from 'src/models/personnel/EmployeeBenefitCodes';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';

import '../BenefitStyleSheet.scss';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import EmployeeBenefits from 'src/models/personnel/EmployeeBenefits';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import FilteredEmployeeBenefits from 'src/models/personnel/FilteredEmployeeBenefits';
import EmployeeBenefitTypeCosts from 'src/models/personnel/EmployeeBenefitTypeCosts';
import QueryYearDialog from '../dialogs/QueryYearDialog';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';
import GenerateBenefitsReportDialog from '../dialogs/benefits/GenerateBenefitsReportDialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ConfirmDeletionDialog from 'src/components/project/sections/tract/dialogs/ConfirmDeletionDialog';

const EmployeeBenefitsPage = () => {
    const { get, post, del, response } = useFetch();
    const { register, setValue, getValues } = useForm();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    // Captured once, at true mount time (lazy initializer, not an effect) - a pending year left by
    // another page (e.g. Year Matrix double-click), same localStorage handoff PersonnelSearch.tsx
    // now uses for the employee itself. Read from state (not an "already applied" ref) so it's
    // reapplied consistently no matter how many times getBenefitYears() ends up firing during
    // mount - this component's own initializeData() fires from two separate effects on the first
    // render, and a one-shot ref guard got consumed by the earlier of those two before
    // PersonnelSearch's async employee fetch had even resolved.
    const [pendingYear] = useState<number | undefined>(() => {
        const stored = localStorage.getItem('personnelPendingYear');
        if (stored) {
            localStorage.removeItem('personnelPendingYear');
            const parsed = parseInt(stored, 10);
            return isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
    });
    const [employeeBenefitCodes, setEmployeeBenefitCodes] = useState<EmployeeBenefitCodes>();
    const [benefitYears, setBenefitYears] = useState<Array<number>>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [allEmployeeBenefitsInformation, setAllEmployeeBenefitsInformation] = useState<Array<EmployeeBenefits>>();
    const [yearlyEmployeeBenefitsInformation, setYearlyEmployeeBenefitsInformation] = useState<FilteredEmployeeBenefits>();
    const [yearlyEmployeeBenefitsTypeCosts, setYearlyEmployeeBenefitsTypeCost] = useState<Array<EmployeeBenefitTypeCosts>>();
    const [selectedEmployeeBenefitsTypeCost, setSelectedEmployeeBenefitsTypeCost] = useState<Array<EmployeeBenefitTypeCosts>>();
    const [selectedBenefit, setSelectedBenefit] = useState<EmployeeBenefits>();
    const [addBenefitTrigger, setAddBenefitTrigger] = useState<number>(0);
    const [editYearNoteTrigger, setEditYearNoteTrigger] = useState<number>(0);

    const dispatch = useDispatch();

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    const onCreate = () => {
        console.log('EmployeeBenefitsPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('EmployeeBenefitsPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // initializing data for personnel
    const initializeData = async () => {
        setIsLoading(true);
        setAllEmployeeBenefitsInformation(null);
        setYearlyEmployeeBenefitsInformation(null);
        setYearlyEmployeeBenefitsTypeCost(null);
        setSelectedEmployeeBenefitsTypeCost(null);
        await getBenefitYears();
        getBenefitCodes();
        if (!selectedPersonnel || !!!selectedPersonnel) {
            setIsLoading(false);
        }
        if (!!selectedPersonnel) {
            getEmployeeBenefits();
        }
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Employee Benefits - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Employee Benefits';
        return title;
    };

    // getting benefit years and mapping for dropdown
    const getBenefitYears = async () => {
        let results = await get(`/v1.0/landmen/benefits/get-benefit-years`);

        if (response.ok && results) {
            let yearsList: Array<number> = results;
            let yearToUse = yearsList[0];

            if (pendingYear !== undefined) {
                yearToUse = pendingYear;
                if (!yearsList.includes(pendingYear)) {
                    // Keep the dropdown's own option list in sync so the <select> has a real
                    // <option> for this year - get-benefit-years is sourced from
                    // EMPLOYEE_BENEFIT_RATES, which isn't guaranteed to include every year an
                    // individual employee's LM_BENEFIT rows span.
                    yearsList = [pendingYear, ...yearsList].sort((a, b) => b - a);
                }
            }

            setBenefitYears(yearsList);
            setValue('year', yearToUse);
        } else {
            showToast('Unable to fetch benefit years', `rgba(255,0,0,0.5)`);
        }
    };

    const benefitYearsDropdown = () => {
        return benefitYears
            ? benefitYears?.map((ct: number) => {
                return { value: ct, label: ct.toString() };
            })
            : [];
    };

    // getting benefit codes
    const getBenefitCodes = async () => {
        let results = await get(`/v1.0/landmen/benefits/get-benefit-categories`);

        if (response.ok) {
            setEmployeeBenefitCodes(results);
        } else {
            console.log(response);
        }
    };

    const updateBenefitRow = () => {
        hideDialog();
        getEmployeeBenefits();
    };

    const toggleAddBenefit = () => {
        if (!yearlyEmployeeBenefitsInformation?.benefits?.[0]?.id) {
            showToast('Please find an employee and a year first.', `rgba(255,0,0,0.5)`);
            return;
        }

        setAddBenefitTrigger((trigger) => trigger + 1);
        dispatch(actions.layout.showDialog(DialogList.ADD_BENEFIT));
    };

    const addBenefitRow = () => {
        hideDialog();
        getEmployeeBenefits();
    };

    // getting initial benefit rates for all years
    const getEmployeeBenefits = async () => {
        let year = getValues()?.year;
        setIsLoading(true);

        let results = await get(`/v1.0/landmen/benefits/${year}/${selectedPersonnel.tsdNo}/get-employee-benefits`);

        if (response.ok) {
            setAllEmployeeBenefitsInformation(results);

            let filteredResults = results.filter((benefit: EmployeeBenefits) => benefit?.year === parseInt(year, 10));

            setValue('notes', filteredResults[0]?.yearNotes);
            setValue('annualPay',
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(filteredResults[0]?.annualPay ?? 0)
            );

            // calculating total costs
            let yearlyEmployeeBenefits = new FilteredEmployeeBenefits();

            yearlyEmployeeBenefits.benefits = filteredResults;

            filteredResults.forEach((benefit: EmployeeBenefits) => {
                yearlyEmployeeBenefits.annualBenefitTotal += benefit.annualTotal;
                yearlyEmployeeBenefits.annualCompanyBenefitTotal += benefit.annualCompanyTotal;
                yearlyEmployeeBenefits.annualEmployeeBenefitTotal += benefit.annualEmployeeTotal;
                yearlyEmployeeBenefits.periodBenefitTotal += benefit.periodTotal;
                yearlyEmployeeBenefits.periodCompanyBenefitTotal += benefit.periodCompanyTotal;
                yearlyEmployeeBenefits.periodEmployeeBenefitTotal += benefit.periodEmployeeTotal;
                yearlyEmployeeBenefits.dayBenefitTotal += benefit.dayTotal;
                yearlyEmployeeBenefits.dayCompanyBenefitTotal += benefit.dayCompanyTotal;
                yearlyEmployeeBenefits.dayEmployeeBenefitTotal += benefit.dayEmployeeTotal;

                yearlyEmployeeBenefits.annualBenefitTotalWithoutPto += benefit.noPtoAnnualTotal;
                yearlyEmployeeBenefits.annualCompanyBenefitTotalWithoutPto += benefit.noPtoAnnualCompanyTotal;
                yearlyEmployeeBenefits.annualEmployeeBenefitTotalWithoutPto += benefit.noPtoAnnualEmployeeTotal;
                yearlyEmployeeBenefits.periodBenefitTotalWithoutPto += benefit.noPtoPeriodTotal;
                yearlyEmployeeBenefits.periodCompanyBenefitTotalWithoutPto += benefit.noPtoPeriodCompanyTotal;
                yearlyEmployeeBenefits.periodEmployeeBenefitTotalWithoutPto += benefit.noPtoPeriodEmployeeTotal;
                yearlyEmployeeBenefits.dayBenefitTotalWithoutPto += benefit.noPtoDayTotal;
                yearlyEmployeeBenefits.dayCompanyBenefitTotalWithoutPto += benefit.noPtoDayCompanyTotal;
                yearlyEmployeeBenefits.dayEmployeeBenefitTotalWithoutPto += benefit.noPtoDayEmployeeTotal;
            });

            yearlyEmployeeBenefits.annualPayTotal = filteredResults[0]?.annualPay;
            yearlyEmployeeBenefits.annualCompanyPayTotal = filteredResults[0]?.annualPay;
            yearlyEmployeeBenefits.annualEmployeePayTotal += 0;
            yearlyEmployeeBenefits.periodPayTotal = filteredResults[0]?.annualPay / 24;
            yearlyEmployeeBenefits.periodCompanyPayTotal = filteredResults[0]?.annualPay / 24;
            yearlyEmployeeBenefits.periodEmployeePayTotal += 0;
            yearlyEmployeeBenefits.dayPayTotal = filteredResults[0]?.annualPay / 260;
            yearlyEmployeeBenefits.dayCompanyPayTotal = filteredResults[0]?.annualPay / 260;
            yearlyEmployeeBenefits.dayEmployeePayTotal += 0;

            yearlyEmployeeBenefits.annualPayAndBenefitTotal = yearlyEmployeeBenefits.annualBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualPayTotal;
            yearlyEmployeeBenefits.annualCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.annualCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualCompanyPayTotal;
            yearlyEmployeeBenefits.annualEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.annualEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualEmployeePayTotal;
            yearlyEmployeeBenefits.periodPayAndBenefitTotal = yearlyEmployeeBenefits.periodBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodPayTotal;
            yearlyEmployeeBenefits.periodCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.periodCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodCompanyPayTotal;
            yearlyEmployeeBenefits.periodEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.periodEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodEmployeePayTotal;
            yearlyEmployeeBenefits.dayPayAndBenefitTotal = yearlyEmployeeBenefits.dayBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayPayTotal;
            yearlyEmployeeBenefits.dayCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.dayCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayCompanyPayTotal;
            yearlyEmployeeBenefits.dayEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.dayEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayEmployeePayTotal;

            setYearlyEmployeeBenefitsInformation(yearlyEmployeeBenefits);

            let typeCosts = await get(`/v1.0/landmen/benefits/${year}/${selectedPersonnel.tsdNo}/get-benfit-type-annual-costs`);

            if (response.ok) {
                setYearlyEmployeeBenefitsTypeCost(typeCosts);

                let filteredCosts = typeCosts?.filter((cost: EmployeeBenefitTypeCosts) => cost.year === parseInt(year, 10));
                setSelectedEmployeeBenefitsTypeCost(filteredCosts);
                setIsLoading(false);
            } else {
                console.log(response);
                showToast('Unable to fetch employee type annual costs: ' + response, `rgba(255,0,0,0.5)`);
                setIsLoading(false);
            }
        } else {
            console.log(response);
            showToast('Unable to fetch employee benefits', `rgba(255,0,0,0.5)`);
            setIsLoading(false);
        }
    };

    // get all benefits information for all years employee has benefits tied to them
    const getFilteredEmployeeBenefits = () => {
        let year = getValues()?.year;
        let filteredResults = allEmployeeBenefitsInformation?.filter((benefit: EmployeeBenefits) => benefit.year === parseInt(year, 10));

        if (filteredResults[0]) {
            setValue('notes', filteredResults[0]?.yearNotes);
            setValue('annualPay',
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(filteredResults[0]?.annualPay ?? 0)
            );
        } else {
            setValue('notes', undefined);
            setValue('annualPay', undefined);
        }

        // calculating total costs
        let yearlyEmployeeBenefits = new FilteredEmployeeBenefits();
        yearlyEmployeeBenefits.benefits = filteredResults;

        filteredResults?.forEach((benefit: EmployeeBenefits) => {
            yearlyEmployeeBenefits.annualBenefitTotal += benefit.annualTotal;
            yearlyEmployeeBenefits.annualCompanyBenefitTotal += benefit.annualCompanyTotal;
            yearlyEmployeeBenefits.annualEmployeeBenefitTotal += benefit.annualEmployeeTotal;
            yearlyEmployeeBenefits.periodBenefitTotal += benefit.periodTotal;
            yearlyEmployeeBenefits.periodCompanyBenefitTotal += benefit.periodCompanyTotal;
            yearlyEmployeeBenefits.periodEmployeeBenefitTotal += benefit.periodEmployeeTotal;
            yearlyEmployeeBenefits.dayBenefitTotal += benefit.dayTotal;
            yearlyEmployeeBenefits.dayCompanyBenefitTotal += benefit.dayCompanyTotal;
            yearlyEmployeeBenefits.dayEmployeeBenefitTotal += benefit.dayEmployeeTotal;

            yearlyEmployeeBenefits.annualBenefitTotalWithoutPto += benefit.noPtoAnnualTotal;
            yearlyEmployeeBenefits.annualCompanyBenefitTotalWithoutPto += benefit.noPtoAnnualCompanyTotal;
            yearlyEmployeeBenefits.annualEmployeeBenefitTotalWithoutPto += benefit.noPtoAnnualEmployeeTotal;
            yearlyEmployeeBenefits.periodBenefitTotalWithoutPto += benefit.noPtoPeriodTotal;
            yearlyEmployeeBenefits.periodCompanyBenefitTotalWithoutPto += benefit.noPtoPeriodCompanyTotal;
            yearlyEmployeeBenefits.periodEmployeeBenefitTotalWithoutPto += benefit.noPtoPeriodEmployeeTotal;
            yearlyEmployeeBenefits.dayBenefitTotalWithoutPto += benefit.noPtoDayTotal;
            yearlyEmployeeBenefits.dayCompanyBenefitTotalWithoutPto += benefit.noPtoDayCompanyTotal;
            yearlyEmployeeBenefits.dayEmployeeBenefitTotalWithoutPto += benefit.noPtoDayEmployeeTotal;
        });

        yearlyEmployeeBenefits.annualPayTotal = filteredResults[0]?.annualPay;
        yearlyEmployeeBenefits.annualCompanyPayTotal = filteredResults[0]?.annualPay;
        yearlyEmployeeBenefits.annualEmployeePayTotal += 0;
        yearlyEmployeeBenefits.periodPayTotal = filteredResults[0]?.annualPay / 24.0;
        yearlyEmployeeBenefits.periodCompanyPayTotal = filteredResults[0]?.annualPay / 24.0;
        yearlyEmployeeBenefits.periodEmployeePayTotal += 0;
        yearlyEmployeeBenefits.dayPayTotal = filteredResults[0]?.annualPay / 260.0;
        yearlyEmployeeBenefits.dayCompanyPayTotal = filteredResults[0]?.annualPay / 260.0;
        yearlyEmployeeBenefits.dayEmployeePayTotal += 0;

        yearlyEmployeeBenefits.annualPayAndBenefitTotal = yearlyEmployeeBenefits.annualBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualPayTotal;
        yearlyEmployeeBenefits.annualCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.annualCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualCompanyPayTotal;
        yearlyEmployeeBenefits.annualEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.annualEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.annualEmployeePayTotal;
        yearlyEmployeeBenefits.periodPayAndBenefitTotal = yearlyEmployeeBenefits.periodBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodPayTotal;
        yearlyEmployeeBenefits.periodCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.periodCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodCompanyPayTotal;
        yearlyEmployeeBenefits.periodEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.periodEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.periodEmployeePayTotal;
        yearlyEmployeeBenefits.dayPayAndBenefitTotal = yearlyEmployeeBenefits.dayBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayPayTotal;
        yearlyEmployeeBenefits.dayCompanyPayAndBenefitTotal = yearlyEmployeeBenefits.dayCompanyBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayCompanyPayTotal;
        yearlyEmployeeBenefits.dayEmployeePayAndBenefitTotal = yearlyEmployeeBenefits.dayEmployeeBenefitTotalWithoutPto + yearlyEmployeeBenefits.dayEmployeePayTotal;

        // filter costs for selected year
        let filteredCosts = yearlyEmployeeBenefitsTypeCosts?.filter((cost: EmployeeBenefitTypeCosts) => cost.year === parseInt(year, 10));
        setSelectedEmployeeBenefitsTypeCost(filteredCosts);
        setYearlyEmployeeBenefitsInformation(yearlyEmployeeBenefits);
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    // start dialogs
    const toggleEditBenefits = (benefit: EmployeeBenefits) => {
        setSelectedBenefit(benefit);
        dispatch(actions.layout.showDialog(DialogList.EDIT_BENEFITS));
    };

    const toggleDeleteBenefit = (benefit: EmployeeBenefits) => {
        if (benefit?.isRequired === 'MAN') {
            showToast('Deletion of Mandatory benefit records is not allowed.', `rgba(255,0,0,0.5)`);
            return;
        }

        setSelectedBenefit(benefit);
        dispatch(actions.layout.showDialog(DialogList.CONFIRM_DELETE));
    };

    const confirmDeleteBenefit = async () => {
        await del(`/v1.0/landmen/benefits/${selectedBenefit.benefitId}/delete-employee-benefit-detail`);

        hideDialog();

        if (response.ok) {
            showToast('Successfully deleted benefit');
            getEmployeeBenefits();
        } else {
            showToast('Unable to delete benefit: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    const toggleQueryYear = () => {
        if (!!selectedPersonnel) {
            dispatch(actions.layout.showDialog(DialogList.QUERY_YEAR));
        } else {
            showToast('Please select a personnel before adding a benefit year', `rgba(255,0,0,0.5)`);
        }
    };

    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const toggleReportGenerator = async () => {
        dispatch(actions.layout.showDialog(DialogList.GENERATE_BENEFITS_REPORT));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };
    // end dialogs

    // generating benefits report
    const generateReport = async (year: string, active: string, tsdNo: string) => {
        if (year === 'current') {
            year = getValues().year;
        }

        showToast('Generating report... ', `rgba(78, 117, 154, 0.5)`, true); // persistent = true

        await get(`/v1.0/landmen/benefits/get-report/${year !== '' ? year : null}/${active !== '' ? active : null}/${tsdNo !== '' ? tsdNo : null}/benefits-overview`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `${year}_Employee_Benefits_Report_${tsdNo !== '' ? selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName : 'All Employees'
                    }${active !== '' ? active === 'Y' ? '_Active' : '_Inactive' : ''}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
                showToast('Report generation successful!');
            });
        } else {
            showToast('Report failed, please check console for details', `rgba(255, 0, 0, 0.5)`);
        }
    };

    // prints the single-employee/year PDF benefits report (BenefitsReport.rdl)
    const printBenefitsReport = async () => {
        if (!selectedPersonnel) {
            showToast('Please select an employee first', `rgba(255,0,0,0.5)`);
            return;
        }

        let year = getValues().year;

        showToast('Generating report... ', `rgba(78, 117, 154, 0.5)`, true); // persistent = true

        await get(`/v1.0/landmen/benefits/${selectedPersonnel.tsdNo}/${year}/get-benefits-report`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `${year}_Benefits_Report_${selectedPersonnel?.fName} ${selectedPersonnel?.lName}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(blobUrl);
                showToast('Report generation successful!');
            });
        } else {
            showToast('Report failed, please check console for details', `rgba(255, 0, 0, 0.5)`);
        }
    };

    // year navigation
    const incrementYear = () => {
        let year = getValues().year;
        if (year < benefitYears[0]) {
            setValue('year', parseInt(year, 10) + 1);

            if (!!selectedPersonnel) {
                getFilteredEmployeeBenefits();
            }
        }
    };

    const decrementYear = () => {
        let year = getValues().year;
        if (year > benefitYears[benefitYears.length - 1]) {
            setValue('year', parseInt(year, 10) - 1);

            if (!!selectedPersonnel) {
                getFilteredEmployeeBenefits();
            }
        }
    };

    // skip to last year with benefits
    const fullIncrementYear = () => {
        let year = getValues().year;
        if (year < benefitYears[0]) {
            setValue('year', benefitYears[0]);

            if (!!selectedPersonnel) {
                getFilteredEmployeeBenefits();
            }
        }
    };

    // skip to first year with benefits
    const fullDecrementYear = () => {
        let year = getValues().year;
        if (year > benefitYears[benefitYears.length - 1]) {
            setValue('year', benefitYears[benefitYears.length - 1]);

            if (!!selectedPersonnel) {
                getFilteredEmployeeBenefits();
            }
        }
    };

    // create new benefit year for employee
    const createNewBenefits = async (year: number) => {
        if (year) {
            await post(`/v1.0/landmen/benefits/${year}/${selectedPersonnel.tsdNo}/add-employee-benefit-year`);

            if (response.ok) {
                getEmployeeBenefits();
            } else {
                showToast(`Unable to create new benefits record for ${year}`, `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast(`Received invalid year from dialog: ${year}`, `rgba(255,0,0,0.5)`);
        }
    };

    // open the Edit Year Note dialog for the entire year
    const toggleEditYearNote = () => {
        if (!yearlyEmployeeBenefitsInformation?.benefits?.[0]?.id) {
            showToast('Please find an employee and a year first.', `rgba(255,0,0,0.5)`);
            return;
        }

        setEditYearNoteTrigger((trigger) => trigger + 1);
        dispatch(actions.layout.showDialog(DialogList.EDIT_YEAR_NOTE));
    };

    const onYearNoteSaved = (notes: string) => {
        setValue('notes', notes);
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={14}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            <div style={{ display: 'flex' }}>
                                <a onClick={() => fullDecrementYear()} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                                    <i style={{ fontSize: '2.5rem' }} className="far fa-angle-double-left clickable" />
                                </a>
                                <a onClick={() => decrementYear()} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                                    <i style={{ fontSize: '2.5rem' }} className="far fa-angle-left clickable" />
                                </a>
                                <a onClick={() => incrementYear()} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                                    <i style={{ fontSize: '2.5rem' }} className="far fa-angle-right clickable" />
                                </a>
                                <a onClick={() => fullIncrementYear()} style={{ paddingLeft: '6px', paddingRight: '6px' }}>
                                    <i style={{ fontSize: '2.5rem' }} className="far fa-angle-double-right clickable" />
                                </a>
                                <div style={{ marginLeft: 'auto' }}>
                                    <a onClick={() => toggleReportGenerator()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Benefit Reports" id="generate-report">
                                        <i style={{ fontSize: '2.5rem' }} className="fa fa-file-excel clickable" />
                                    </a>
                                    <a onClick={() => printBenefitsReport()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Print Benefits Report">
                                        <i style={{ fontSize: '2.5rem' }} className="far fa-file-pdf clickable" />
                                    </a>
                                    <a onClick={() => toggleQueryYear()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Add Benefit Year">
                                        <i style={{ fontSize: '2.5rem' }} className="far fa-plus-circle clickable" />
                                    </a>
                                    <a onClick={() => toggleAddBenefit()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Add Benefit">
                                        <i style={{ fontSize: '2.5rem' }} className="far fa-plus-square clickable" />
                                    </a>
                                </div>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <Page>
                <Page.Section width={14}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel>
                            <form>
                                <div className="form-row">
                                    <RHFSelect
                                        name="year"
                                        label="Year"
                                        labelOnLeft={true}
                                        options={benefitYearsDropdown()}
                                        register={register}
                                        onChange={() => getFilteredEmployeeBenefits()}
                                    />
                                    <RHFInputBox
                                        name="notes"
                                        label="Notes"
                                        labelOnLeft={true}
                                        register={register}
                                        disabled={true}
                                        bodyStyle={{ flexGrow: 1 }}
                                        style={{ width: '100%' }}
                                    />
                                    <a onClick={() => toggleEditYearNote()} style={{ padding: '0 6px', alignSelf: 'center' }} title="Edit Year Note">
                                        <i style={{ fontSize: '1.5rem' }} className="far fa-edit clickable" />
                                    </a>
                                    <RHFInputBox
                                        name="annualPay"
                                        label="Annual Pay"
                                        labelOnLeft={true}
                                        register={register}
                                        disabled={true}
                                    />
                                </div>
                            </form>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            {isLoading && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50vh',
                        width: '100%',
                    }}
                >
                    <LoadingSpinner />
                </div>
            )}
            <Page>
                <Page.Section width={14}>
                    {selectedEmployeeBenefitsTypeCost?.length > 0 &&
                        <Panel backgroundColor="#D3D3D3">
                            <Panel>
                                <div style={{ overflowY: 'scroll', overflowX: 'scroll', maxHeight: '75rem' }}>
                                    {/* top table */}
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr style={{ background: '#d6d6d6' }}>
                                                <th style={{ width: '5%' }} className="sticky-header">Type</th>
                                                <th style={{ width: '7%' }} className="sticky-header">Category</th>
                                                <th style={{ width: '3%' }} className="sticky-header">M/O</th>
                                                <th style={{ width: '4%' }} className="sticky-header">Code</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Code Desc</th>
                                                <th style={{ width: '2%' }} className="sticky-header">T/F</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Flat Rate</th>
                                                <th style={{ width: '8%' }} className="sticky-header">Notes</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Annual Total</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Annual Company</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Annual Employee</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Period Total</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Period Company</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Period Employee</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Daily Total</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Daily Company</th>
                                                <th style={{ width: '6%' }} className="sticky-header">Daily Employee</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {yearlyEmployeeBenefitsInformation?.benefits?.map((row: EmployeeBenefits, index: number) => {
                                                return (
                                                    <tr key={index} className={`contract-pay-history-active-item-${index}`}>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.type}
                                                        </td>
                                                        <td style={{ width: '7%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.category}
                                                        </td>
                                                        <td style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.isRequired}
                                                        </td>
                                                        <td style={{ width: '4%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.code}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.description}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.method}
                                                        </td>
                                                        <td style={{ width: '2%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.flatAmount)}
                                                        </td>
                                                        <td style={{ width: '8%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {row.benefitNotes}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.annualTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.annualCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.annualEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.periodTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.periodCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.periodEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.dayTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.dayCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row?.dayEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '18%', textAlign: 'center' }}>
                                                            <Button
                                                                text={''}
                                                                title="Edit Benefits"
                                                                reactIcon={FaEdit}
                                                                iconHoverColor="lightblue"
                                                                iconStyle={{ paddingRight: '1%', fontSize: '2.0rem', color: '#4e759a' }}
                                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                                onClick={() => toggleEditBenefits(row)}
                                                            />
                                                            <Button
                                                                text={''}
                                                                title="Delete Benefit"
                                                                reactIcon={FaTrash}
                                                                iconHoverColor="lightblue"
                                                                iconStyle={{ paddingRight: '1%', fontSize: '2.0rem', color: '#4e759a' }}
                                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                                onClick={() => toggleDeleteBenefit(row)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            <br />
                                            {/* category totals */}
                                            {selectedEmployeeBenefitsTypeCost?.map((row: EmployeeBenefitTypeCosts, index: number) => {
                                                return (
                                                    <tr key={index}>
                                                        <td style={{ width: '5%' }} />
                                                        <td style={{ width: '7%' }} />
                                                        <td style={{ width: '3%' }} />
                                                        <td style={{ width: '4%' }} />
                                                        <td style={{ width: '5%' }} />
                                                        <td style={{ width: '5%' }} />
                                                        <td style={{ width: '2%' }} />
                                                        <td style={{ width: '8%' }}>
                                                            <b>{row.type}</b>
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.annualTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.annualCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.annualEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.periodTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.periodCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.periodEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.dayTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.dayCompanyTotal)}
                                                        </td>
                                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(row.dayEmployeeTotal)}
                                                        </td>
                                                        <td style={{ width: '18%' }} />
                                                    </tr>

                                                );
                                            })}
                                            <br />
                                            {/* bottom table with final totals */}
                                            <tr>
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '7%' }} />
                                                <td style={{ width: '3%' }} />
                                                <td style={{ width: '4%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '2%' }} />
                                                <td style={{ width: '8%' }}>
                                                    <b>Benefit Total</b>
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualCompanyBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualEmployeeBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodCompanyBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodEmployeeBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayCompanyBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayEmployeeBenefitTotal)}
                                                </td>
                                                <td style={{ width: '18%' }} />
                                            </tr>
                                            <tr>
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '7%' }} />
                                                <td style={{ width: '3%' }} />
                                                <td style={{ width: '4%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '2%' }} />
                                                <td style={{ width: '8%' }}>
                                                    <b>Benefit Total w/o PTO</b>
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualCompanyBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualEmployeeBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodCompanyBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodEmployeeBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayCompanyBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayEmployeeBenefitTotalWithoutPto)}
                                                </td>
                                                <td style={{ width: '18%' }} />
                                            </tr>
                                            <tr>
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '7%' }} />
                                                <td style={{ width: '3%' }} />
                                                <td style={{ width: '4%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '2%' }} />
                                                <td style={{ width: '8%' }}>
                                                    <b>Pay Total</b>
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualCompanyPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualEmployeePayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodCompanyPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodEmployeePayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayCompanyPayTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayEmployeePayTotal)}
                                                </td>
                                                <td style={{ width: '18%' }} />
                                            </tr>
                                            <tr>
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '7%' }} />
                                                <td style={{ width: '3%' }} />
                                                <td style={{ width: '4%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '5%' }} />
                                                <td style={{ width: '2%' }} />
                                                <td style={{ width: '8%' }}>
                                                    <b>Pay / Benefit Total</b>
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualCompanyPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.annualEmployeePayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodCompanyPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.periodEmployeePayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayCompanyPayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    }).format(yearlyEmployeeBenefitsInformation?.dayEmployeePayAndBenefitTotal)}
                                                </td>
                                                <td style={{ width: '18%' }} />
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <br />
                            </Panel>
                        </Panel>
                    }
                    {yearlyEmployeeBenefitsInformation?.benefits?.length <= 0 &&
                        <h2 style={{ textAlign: 'center' }}>No benefits found for {getValues().year ?? 'selected year'}</h2>
                    }
                </Page.Section>
            </Page>
            <EditBenefitDialog
                dialogName={DialogList.EDIT_BENEFITS}
                hideDialog={hideDialog}
                updateBenefit={updateBenefitRow}
                showToast={showToast}
                benefit={selectedBenefit}
                benefitCodes={employeeBenefitCodes}
            />
            <AddBenefitDialog
                dialogName={DialogList.ADD_BENEFIT}
                hideDialog={hideDialog}
                addBenefit={addBenefitRow}
                showToast={showToast}
                benefitCodes={employeeBenefitCodes}
                lbId={yearlyEmployeeBenefitsInformation?.benefits?.[0]?.id}
                resetTrigger={addBenefitTrigger}
            />
            <EditYearNoteDialog
                dialogName={DialogList.EDIT_YEAR_NOTE}
                hideDialog={hideDialog}
                showToast={showToast}
                lbId={yearlyEmployeeBenefitsInformation?.benefits?.[0]?.id}
                notes={getValues().notes}
                resetTrigger={editYearNoteTrigger}
                onSaved={onYearNoteSaved}
            />
            <ConfirmDeletionDialog
                dialogName={DialogList.CONFIRM_DELETE}
                hideDialog={hideDialog}
                body={`Are you sure you want to remove the ${selectedBenefit?.description ?? 'selected'} benefit?`}
                confirmAction={confirmDeleteBenefit}
            />
            <QueryYearDialog
                dialogName={DialogList.QUERY_YEAR}
                defaultYear={benefitYears ? benefitYears[0] : undefined}
                hideDialog={hideDialog}
                sendYear={createNewBenefits}
                dialogTitle={'Add Employee Benefit Year'}
            />
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
            <GenerateBenefitsReportDialog
                dialogName={DialogList.GENERATE_BENEFITS_REPORT}
                benefitYears={benefitYears}
                onSubmit={generateReport}
                hideDialog={hideDialog}
                selectedTsdNo={selectedPersonnel?.tsdNo}
                currentYear={getValues().year}
            />
        </div>
    );
};

export default EmployeeBenefitsPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeeBenefitsPage.tsx